import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, GripVertical } from 'lucide-react';
import type { ItemMilestone } from '@/schemas/milestone';
import { AssigneeBadge } from '@/components/items/AssigneeBadge';
import type { TeamMember } from '@/hooks/useProjectTeamMembers';

interface SortableMilestoneCardProps {
  milestone: ItemMilestone;
  index: number;
  isReorderMode: boolean;
  onEdit: (milestone: ItemMilestone) => void;
  onDelete: (id: string, name: string) => void;
  canEdit: boolean;
  canDelete: boolean;
  getTypeColor: (typeName: string) => string;
  getTypeDisplayName: (typeName: string) => string;
  getStatusVariant: (status: ItemMilestone['status']) => 'default' | 'secondary' | 'outline' | 'destructive';
  teamMembers?: TeamMember[];
}

export const SortableMilestoneCard = ({
  milestone,
  index,
  isReorderMode,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  getTypeColor,
  getTypeDisplayName,
  getStatusVariant,
  teamMembers = [],
}: SortableMilestoneCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: milestone.id, disabled: !isReorderMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`hover:bg-muted/50 transition-colors ${isDragging ? 'z-50 shadow-lg' : ''}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Số thứ tự và drag handle */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {isReorderMode && (
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
              >
                <GripVertical className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
              {index + 1}
            </div>
          </div>

          {/* Milestone content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: getTypeColor(milestone.milestone_type) }}
              />
              <h4 className="font-medium truncate">{milestone.name}</h4>
              {/* Assignee avatar */}
              <AssigneeBadge 
                assignedUserId={milestone.assigned_user_id} 
                teamMembers={teamMembers} 
              />
            </div>
            
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mb-2">
              <span>{getTypeDisplayName(milestone.milestone_type)}</span>
              <span>•</span>
              <span>{milestone.start_date} to {milestone.end_date}</span>
              <span>•</span>
              <span>{milestone.completion_percentage}% complete</span>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant={getStatusVariant(milestone.status)}>
                {milestone.status.replace('_', ' ')}
              </Badge>
            </div>

            {milestone.notes && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {milestone.notes}
              </p>
            )}
          </div>

          {/* Action buttons */}
          {!isReorderMode && (
            <div className="flex gap-2 flex-shrink-0">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(milestone)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(milestone.id, milestone.name)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
